import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTags,
  faMapMarkedAlt,
  faCalendarAlt,
  faCoins,
  faStar,
  faUsers,
  faBriefcase,
  faLock,
  faArrowRight,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { R5Section, R5Card } from "./ui";
import { useAuth } from "../contexts/CombinedAuthContext";
import { usePlans } from "../contexts/PlansContext";
import { useBookmarks } from "./Bookmarks";
import { tierGrantsPerk, favoriteCapFor } from "../constants/plans";
import "../styles/MemberResources.css";

const WELCOME_KEY = "guild.welcomeSeen";

/**
 * MemberResources — the signed-in member's "here's what you can do right now"
 * orientation panel, shown at the top of the home page for authenticated
 * users. Resources are gated by the member's active tier (driven by the same
 * plans.js perk data as the rest of the app), so a free Deckhand sees what
 * they have and what an upgrade unlocks. Renders nothing for logged-out
 * visitors (the marketing home already serves them).
 *
 * The first-login welcome is a one-time dismissible intro (localStorage
 * `guild.welcomeSeen`) rather than a blocking modal — the panel itself stays
 * every visit as the persistent answer to "what do I have access to?".
 */
export default function MemberResources() {
  const { isAuthenticated, user } = useAuth();
  const plans = usePlans() || {};
  const bookmarks = useBookmarks() || {};

  const [showWelcome, setShowWelcome] = useState(() => {
    try {
      return localStorage.getItem(WELCOME_KEY) !== "1";
    } catch (_e) {
      return false;
    }
  });

  if (!isAuthenticated) return null;

  const dismissWelcome = () => {
    try {
      localStorage.setItem(WELCOME_KEY, "1");
    } catch (_e) {
      /* ignore */
    }
    setShowWelcome(false);
  };

  const activeTier =
    typeof plans.getActiveTravelerTier === "function"
      ? plans.getActiveTravelerTier()
      : null;
  const tierName = activeTier?.name || "Traveler";
  const isBusinessMember =
    typeof plans.isBusinessMember === "function" && plans.isBusinessMember();
  const hasPaid =
    typeof plans.hasPaidSubscription === "function" && plans.hasPaidSubscription();

  const favCount = typeof bookmarks.count === "number" ? bookmarks.count : 0;
  const favCap = favoriteCapFor(activeTier);

  const firstName =
    user?.member?.profile?.nickname ||
    (user?.member?.email ? user.member.email.split("@")[0] : "") ||
    "friend";

  // Each resource links to a destination and is either granted for the current
  // tier (live link) or shown locked with what unlocks it. `perk` is checked
  // against the tier's perks; omit `perk` for always-available resources.
  const resources = [
    {
      key: "deals",
      icon: faTags,
      title: "Browse partner deals",
      desc: "Discounts at participating island businesses.",
      to: "/business-listings",
      granted: true,
    },
    {
      key: "map",
      icon: faMapMarkedAlt,
      title: "Open the Treasure Map",
      desc: tierGrantsPerk(activeTier, "fullMap")
        ? "The full interactive map of the islands."
        : "The digital map of partners and spots.",
      to: "/pirate-map",
      granted: true,
    },
    {
      key: "events",
      icon: faCalendarAlt,
      title: "See island events",
      desc: "What's happening around the USVI.",
      to: "/events",
      granted: true,
    },
    {
      key: "doubloons",
      icon: faCoins,
      title: "Earn Doubloons",
      desc: "Rewards for visits, referrals, and treasure hunts.",
      to: "/doubloons",
      granted: true,
    },
    {
      key: "favorites",
      icon: faStar,
      title: "Save favorites",
      desc: `Your Treasure Chest — ${favCount}/${favCap} saved.`,
      to: "/treasure-chest",
      granted: true,
    },
    {
      key: "roommates",
      icon: faUsers,
      title: "Roommate & job finder",
      desc: tierGrantsPerk(activeTier, "roommateJobs")
        ? "Find roommates and island opportunities."
        : "Unlocks with Traveler Plus.",
      to: "/roommate-finder",
      granted: tierGrantsPerk(activeTier, "roommateJobs"),
      unlockWith: "Traveler Plus",
    },
  ];

  if (isBusinessMember) {
    resources.push({
      key: "business",
      icon: faBriefcase,
      title: "Business dashboard",
      desc: "Manage your listings and track performance.",
      to: "/business-dashboard",
      granted: true,
    });
  }

  return (
    <R5Section
      className="member-resources"
      title="Your Guild"
      meta={`You're a ${tierName}`}
    >
      {showWelcome && (
        <div className="member-welcome" role="note">
          <div className="member-welcome__text">
            <strong>Welcome aboard, {firstName}!</strong> You've joined as a{" "}
            {tierName}. Here's everything you can do right now — tap any card to
            jump in.
          </div>
          <button
            type="button"
            className="member-welcome__dismiss"
            onClick={dismissWelcome}
            aria-label="Dismiss welcome"
          >
            <FontAwesomeIcon icon={faTimes} /> Got it
          </button>
        </div>
      )}

      <div className="member-resources__grid">
        {resources.map((r) => {
          const body = (
            <div className="r5-card__body member-resource__body">
              <FontAwesomeIcon icon={r.icon} className="member-resource__icon" />
              <div className="member-resource__text">
                <h3 className="member-resource__title">
                  {r.title}
                  {!r.granted && (
                    <FontAwesomeIcon icon={faLock} className="member-resource__lock" />
                  )}
                </h3>
                <p className="member-resource__desc">{r.desc}</p>
              </div>
              {r.granted && (
                <FontAwesomeIcon icon={faArrowRight} className="member-resource__go" />
              )}
            </div>
          );

          return (
            <R5Card key={r.key}>
              {r.granted ? (
                <Link to={r.to} className="member-resource member-resource--link">
                  {body}
                </Link>
              ) : (
                <Link
                  to="/traveler-passes"
                  className="member-resource member-resource--locked"
                  title={`Unlock with ${r.unlockWith}`}
                >
                  {body}
                </Link>
              )}
            </R5Card>
          );
        })}
      </div>

      {!hasPaid && (
        <div className="member-resources__upgrade">
          Want more perks?{" "}
          <Link to="/traveler-passes">
            Upgrade your pass <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      )}
    </R5Section>
  );
}
